/**
 * Configuration Supabase pour l'application de planning
 */

import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key'

export const isDemoMode = !import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY

if (isDemoMode) {
  console.warn('Missing Supabase environment variables. Running in demo mode.')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Désactive navigator.locks. Sur auth-js 2.105+ le default a un
    // lockAcquireTimeout de 5s : avec le double listener auth (App.tsx +
    // useAuth.ts) qui lance plusieurs queries concurrentes au login, le
    // lock timeout déclenche un AbortError ("The lock request is aborted")
    // qui remonte jusqu'à l'UI ("Erreur chargement profil"). Sans coordination
    // navigator.locks, deux tabs peuvent en théorie se battre pour refresh
    // simultanément (l'une recevant "Already Used") — compromis accepté car
    // le scénario est rare et localement réparable par un reload.
    lock: (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => fn(),
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'X-Client-Info': 'planning-ecole-saas',
    },
  },
})

/**
 * Client isolé (sans persistance de session) pour les opérations
 * qui ne doivent pas perturber la session principale (ex: vérification de mot de passe,
 * création de compte enseignant via signUp fallback).
 */
export const isolatedClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: 'sb-isolated-auth',
    flowType: 'implicit',
  },
})

// Types utilitaires pour Supabase
export type SupabaseClient = typeof supabase